import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { OnlyFansSessionActionType } from '../types/onlyFansDbTypes';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { SextforceAutoRepostColor } from './useSextforceAutoRepost';
import useSubscriber from './useSubscriber';

export type PriorityMessageAdjustUnit = 'minutes' | 'hours' | 'days' | 'weeks';

export interface ScheduledTask {
    _id?: string;
    subscriberId: string;
    actionType: OnlyFansSessionActionType;
    colorId?: string;
    color?: SextforceAutoRepostColor;
    scheduleDateTime: Date;
    inactive?: boolean;
    complete: boolean;
    repeatEveryAmount?: number;
    repeatEveryUnit?: moment.unitOfTime.DurationConstructor;
    endDate?: Date;
    payload?: any;
}

const useMessageUsersScheduledSessions = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const params = useParams();
    const queryClient = useQueryClient();
    const axios = useAxios();

    const { data: subscriber } = useSubscriber();

    const [isUpdatingScheduledMessage, setIsUpdatingScheduledMessage] = useState<boolean>(false);
    const [isDeletingScheduledMessage, setIsDeletingScheduledMessage] = useState<boolean>(false);
    const [bulkDeleteScheduledMessagesLoading, setBulkDeleteScheduledMessagesLoading] = useState<boolean>(false);
    const [bulkDistributeScheduledMessagesLoading, setBulkDistributeScheduledMessagesLoading] = useState<boolean>(false);
    const [bulkAdjustScheduledMessagesLoading, setBulkAdjustScheduledMessagesLoading] = useState<boolean>(false);

    // Fetch Scheduled Messages
    const fetchScheduledMessages = async (): Promise<ScheduledTask[]> => {
        const query: string = `${settingsContext.routes.messageUsers.base}${params.userId}/scheduled`;

        return axios
            .get(query)
            .then(async response => {
                if (response.status === 200) {
                    return response.data;
                } else {
                    return Promise.reject(response.data);
                }
            })
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);
            });
    };

    const updateScheduledMessage = async (messageId: string, payload: any): Promise<ScheduledTask | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.messageUsers.base}${params.userId}/scheduled/${messageId}`;

            setIsUpdatingScheduledMessage(true);

            return axios
                .put(query, payload)
                .then(async response => {
                    if (response.status === 200) {
                        queryClient.invalidateQueries(['messageUsersScheduledMessages', subscriber._id]);

                        setIsUpdatingScheduledMessage(false);

                        return response.data;
                    } else {
                        setIsUpdatingScheduledMessage(false);

                        return Promise.reject(response.data);
                    }
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);

                    setIsUpdatingScheduledMessage(false);

                    return Promise.reject(error);
                });
        }

        return null;
    };

    const setScheduleMessageInactiveStatus = async (messageId: string, inactive: boolean): Promise<ScheduledTask | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.messageUsers.base}${params.userId}/scheduled/${messageId}/status`;

            setIsUpdatingScheduledMessage(true);

            return axios
                .patch(query, { inactive })
                .then(async response => {
                    if (response.status === 200) {
                        queryClient.invalidateQueries(['messageUsersScheduledMessages', subscriber._id]);

                        setIsUpdatingScheduledMessage(false);

                        return response.data;
                    } else {
                        setIsUpdatingScheduledMessage(false);

                        return Promise.reject(response.data);
                    }
                })
                .catch(error => {
                    console.error(error);

                    setIsUpdatingScheduledMessage(false);

                    return Promise.reject(error);
                });
        }

        return null;
    };

    const deleteScheduledMessage = async (messageId: string): Promise<{ success: true; id: string } | void> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.messageUsers.base}${params.userId}/scheduled/${messageId}`;

            setIsDeletingScheduledMessage(true);

            return axios
                .delete(query)
                .then(async response => {
                    setIsDeletingScheduledMessage(false);

                    if (response.status === 200) {
                        queryClient.invalidateQueries(['messageUsersScheduledMessages', subscriber?._id]);

                        return response.data;
                    } else {
                        return Promise.reject(response.data);
                    }
                })
                .catch(error => {
                    setIsDeletingScheduledMessage(false);

                    console.error(error);
                    handleHttpError(error, dialog);
                });
        }
    };

    const bulkDeleteScheduledMessages = async (scheduledTaskIds: string[]): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.messageUsers.base}${params.userId}/bulkDelete`;

        setBulkDeleteScheduledMessagesLoading(true);

        const data = await axios
            .delete(url, {
                data: {
                    scheduledTaskIds,
                },
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkDeleteScheduledMessagesLoading(false);
            });

        return data;
    };

    const bulkDistributeScheduledMessages = async (
        scheduledTaskIds: string[],
        offsetAmount: number,
        offsetUnit: string,
        startDate: Date,
    ): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.messageUsers.base}${params.userId}/bulkDistribute`;

        setBulkDistributeScheduledMessagesLoading(true);

        const data = await axios
            .post(url, {
                scheduledTaskIds,
                offsetAmount,
                offsetUnit,
                startDate,
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkDistributeScheduledMessagesLoading(false);
            });

        return data;
    };

    const bulkAdjustScheduledMessages = async (
        scheduledTaskIds: string[],
        offsetAmount: number,
        offsetUnit: PriorityMessageAdjustUnit,
    ): Promise<{ success: boolean; adjustedCount?: number }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.messageUsers.base}${params.userId}/bulkAdjust`;

        setBulkAdjustScheduledMessagesLoading(true);

        const data = await axios
            .post(url, {
                scheduledTaskIds,
                offsetAmount,
                offsetUnit,
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkAdjustScheduledMessagesLoading(false);
            });

        return data;
    };

    const messageUsersScheduledSessions = useQuery(['messageUsersScheduledMessages', subscriber?._id], () => fetchScheduledMessages(), {
        refetchOnWindowFocus: false,
        staleTime: 5000,
        enabled:
            userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber && subscriber._id
                ? true
                : false,
    });

    return {
        ...messageUsersScheduledSessions,
        setScheduleMessageInactiveStatus,
        updateScheduledMessage,
        isUpdatingScheduledMessage,
        deleteScheduledMessage,
        isDeletingScheduledMessage,
        bulkDeleteScheduledMessages,
        bulkDeleteScheduledMessagesLoading,
        bulkDistributeScheduledMessages,
        bulkDistributeScheduledMessagesLoading,
        bulkAdjustScheduledMessages,
        bulkAdjustScheduledMessagesLoading,
    };
};

export default useMessageUsersScheduledSessions;
